import React from "react";
import {
  Box,
  Typography,
  Stack,
  Switch,
  Chip,
} from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { MapPin } from "lucide-react";
import { useTheme } from "../../../../context/ThemeContext";
import { formatTime, formatEur, formatDistance, getTransportIcon } from "./transportUtils";

export const TransportStepRow = ({
  step,
  index,
  isLast,
  isEnabled,
  canToggle,
  onToggle,
}) => {
  const muiTheme = useMuiTheme();
  const { isDark } = useTheme();

  const StepIcon = getTransportIcon(step.type);
  const isBus = step.type === "bus-transport";
  const disabled = !isEnabled;

  const getName = (type) => {
    switch (type) {
      case "car":
        return "Autokuljetus";
      case "wait":
        return "Bussinvaihto";
      default:
        return type || "Kuljetus";
    }
  }
  return (
    <Stack direction="row" spacing={1.5} sx={{ position: "relative" }}>
      {/* ── Timeline rail ── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: 26,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            bgcolor: disabled
              ? "action.disabledBackground"
              : isDark
                ? "rgba(255,255,255,0.08)"
                : "grey.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: disabled ? 0.5 : 1,
            transition: "opacity 0.2s",
          }}
        >
          <StepIcon size={14} color={disabled ? undefined : isBus ? muiTheme.palette.primary.main : undefined} />
        </Box>
        {!isLast && (
          <Box
            sx={{
              width: 2,
              flex: 1,
              bgcolor: disabled ? "action.disabledBackground" : "divider",
              my: 0.5,
              transition: "background 0.2s",
            }}
          />
        )}
      </Box>

      {/* ── Step content ── */}
      <Box
        sx={{
          flex: 1,
          pb: isLast ? 0 : 2,
          opacity: disabled ? 0.45 : 1,
          transition: "opacity 0.2s",
        }}
      >
        {/* Header row: name + time + price + toggle */}
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          {/* Left: name */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                textDecoration: disabled ? "line-through" : "none",
              }}
              noWrap
            >
              {step.shortName || step.exactName || getName(step.type)}
            </Typography>
            {step.exactName && step.shortName && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                {step.exactName}
              </Typography>
            )}
          </Box>

          {/* Right: time + price + toggle */}
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
              {formatTime(step.start)} – {formatTime(step.end)}
            </Typography>

            {step.price != null && (
              <Chip
                label={formatEur(step.price)}
                size="small"
                variant={disabled ? "outlined" : "filled"}
                color={disabled ? "default" : "primary"}
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            )}

            {canToggle && (
              <Switch
                checked={isEnabled}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggle(index);
                }}
                size="small"
                sx={{
                  ml: 0.5,
                  "& .MuiSwitch-switchBase": { p: 0.4 },
                  "& .MuiSwitch-thumb": { width: 14, height: 14 },
                  "& .MuiSwitch-track": { borderRadius: 8 },
                  width: 36,
                  height: 22,
                }}
              />
            )}
          </Stack>
        </Stack>

        {/* Route */}
        <Stack spacing={0.25} sx={{ mt: 0.75 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <MapPin size={12} color={muiTheme.palette.success.main} style={{ flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {step.origin}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <MapPin size={12} color={muiTheme.palette.error.main} style={{ flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {step.destination}
            </Typography>
          </Stack>
        </Stack>

        {/* Distance if available */}
        {step.distance && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25, display: "block" }}>
            {formatDistance(step.distance)}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};
